import { useEffect } from 'react';
import RatingOpinion from '../../assets/img/rating-opinion.svg?react';

import { Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  setRatingPayload,
  filterActiveRating,
  openRatingModal,
} from './RatingSlice';

import { CloseButton } from './CloseButton';
import { RatingPortal } from './RatingPortal';
import RatingStar from './RatingStar';
import { WButton } from '@wareclouds/wareclouds-ui';

import { useGetRatings, useConfirmRating } from './services';
import { useHandleGetRatingSuccess } from './utils';

import debounce from '../../utils/debounce';
import { Trans, useTranslation } from 'react-i18next';

function Rating() {
  const { t } = useTranslation(['title', 'description', 'glossary']);
  const dispatch = useDispatch();
  const { isLoading, isSuccess, data } = useGetRatings();
  useHandleGetRatingSuccess({ isSuccess, data });
  const { isPending: isUpdatingRating, mutateAsync } = useConfirmRating();
  const { isOpen, payload, activeRating, ratings } = useSelector(
    (store) => store.rating
  );

  const receiverName =
    `${activeRating?.user?.first_name} ${activeRating?.user?.last_name}` ?? '';

  const onRatingChange = (value) => {
    dispatch(setRatingPayload({ rating: value }));
  };

  const onCommentChange = (comment) => {
    if (comment) dispatch(setRatingPayload({ ...payload, comment }));
    else dispatch(setRatingPayload({ rating: payload.rating }));
  };

  const saveRating = async (ratingData) => {
    const { idRating, payload, skipped } = ratingData;
    await mutateAsync({ idRating, payload, skipped });
  };

  useEffect(() => {
    if (!isLoading && ratings.length) {
      debounce(dispatch)(filterActiveRating());
    }

    if (typeof activeRating === 'object' && Object.keys(activeRating).length)
      dispatch(openRatingModal());
  }, [dispatch, isLoading, activeRating, ratings]);

  return (
    <RatingPortal isOpen={isOpen} className="rating">
      <header className="d-flex flex-column justify-content-center align-items-center">
        <CloseButton
          className="align-self-end"
          disabled={isUpdatingRating}
          onClick={() =>
            saveRating({
              idRating: activeRating.id,
              payload: { rating: null },
              skipped: true,
            })
          }
        />
        <RatingOpinion />
        <p className="fs-4 m-1 fw-bold text-center">
          {t('rating.modal.active', { ns: 'title' })}
        </p>
        <p className="m-1 fw-bold text-center">
          <Trans
            t={t}
            i18nKey="rating.modal.active"
            ns="description"
            name={receiverName}
          >
            How would you rate your interaction with{' '}
            <span className="text-primary">{{ name: receiverName }}</span>?
          </Trans>
        </p>
      </header>
      <section className="px-3">
        <div className="d-flex justify-content-center align-items-center my-4">
          <RatingStar
            rating={payload.rating ?? 0}
            onRatingChange={onRatingChange}
          />
        </div>

        <Form.Group className="my-3">
          <Form.Label>
            <span className="fw-bold">{t('comments', { ns: 'glossary' })}</span>{' '}
            (opcional)
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            maxLength={200}
            value={payload.comment}
            onChange={(e) => onCommentChange(e.target.value)}
          />
        </Form.Group>
      </section>

      <footer className="d-flex justify-content-center align-items-center">
        <WButton
          type="button"
          fullWidth
          variant="contained"
          isDisabled={!payload.rating}
          onClick={() =>
            saveRating({ idRating: activeRating.id, payload, skipped: false })
          }
          label={t('confirm', { ns: 'button' })}
          isLoading={isUpdatingRating && !!payload.rating}
          isSubmittingLabel={`${t('processing', { ns: 'glossary' })}...`}
        />
      </footer>
    </RatingPortal>
  );
}

export default Rating;
